import { DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { A11yModule, LiveAnnouncer } from '@angular/cdk/a11y';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { Subject, Subscription, debounceTime, distinctUntilChanged, of, switchMap } from 'rxjs';
import { HelpArticle, HelpService, rotulosDePermissao } from '@core/services/help';
import { HelpTelemetryService } from '@core/services/help/help-telemetry.service';
import { TutorialService } from '@core/tutorial/tutorial.service';
import { HelpContentComponent } from '@shared/components/help-content/help-content.component';
import { ButtonComponent } from '@shared/components/buttons/button/button.component';
import { SupportEmptyStateComponent } from '@shared/components/support/empty-state/support-empty-state.component';
import { SupportStatusChipComponent } from '@shared/components/support/status-chip/support-status-chip.component';
import { SELOS } from '@shared/components/support/support-selos';
import { SupportHubService, SupportService } from 'src/app/views/pages/tenant/support/services';
import {
  HelpFeedbackReason, HelpInteractionPayload, IncidentSeverity, SupportIncident, SupportStatus, SupportStatusState,
  ordenarIncidentesPorGravidade,
} from '@model/support';

function semItem(conjunto: ReadonlySet<string>, item: string): ReadonlySet<string> {
  const copia = new Set(conjunto);
  copia.delete(item);
  return copia;
}

const ANUNCIO_DO_RETORNO = 'Formulário de retorno aberto. Escolha o motivo e conte mais, se quiser.';
const ANUNCIO_DO_RELATO = 'Formulário de relato aberto. Descreva o erro desta página, se quiser.';

const TAMANHO_NESTA_TELA = 5;
const TAMANHO_RESULTADOS = 8;
const ATRASO_SEM_RESULTADO_MS = 1500;
const CARACTERES_MINIMOS_PARA_SEM_RESULTADO = 3;

interface LinhaDeStatus {
  categoria: 'sucesso' | 'info' | 'atencao' | 'perigo';
  texto: string;
  comIncidente: boolean;
  /**
   * Achado da revisão (rodada 5, baixa, achado 7): a §6.1 pede "selo da
   * severidade + título + Ver status" e o P6 diz que nenhuma cor aparece sem
   * texto — sem o selo, um título sem a palavra da severidade ("Emissão de
   * notas fiscais") não dizia se era instabilidade ou indisponibilidade.
   * `null` quando não há incidente, ou quando o título já começa com o
   * rótulo da severidade (mesma regra de `support-incident-banner`).
   */
  severidade: IncidentSeverity | null;
}

const CATEGORIA_POR_ESTADO: Record<SupportStatusState, LinhaDeStatus['categoria']> = {
  operacional: 'sucesso',
  informativo: 'info',
  instabilidade: 'atencao',
  indisponibilidade: 'perigo',
};

const FRASE_POR_ESTADO: Record<SupportStatusState, string> = {
  operacional: 'Todos os serviços funcionam normalmente.',
  informativo: 'Há um aviso da equipe. Veja abaixo.',
  instabilidade: 'Parte do sistema está lenta ou intermitente.',
  indisponibilidade: 'Parte do sistema está fora do ar.',
};

/**
 * Painel "Ajuda e suporte" (spec visual §6.1). Substitui, só no tenant, o
 * `app-help-button` e o botão de atendente do cabeçalho: um único ícone com
 * busca em destaque, artigos da rota atual, leitura inline e atalhos.
 *
 * Mora em `layout/` (não em `shared/components/support/hub/`, o endereço que
 * a spec sugere) porque esta etapa (2A) não altera arquivos do kit
 * compartilhado com o admin (F3) — ver `desvios_do_brief` do relatório.
 *
 * Estado (aberto/artigo-alvo) vem de `SupportHubService`, injetável por
 * qualquer tela (o cabeçalho abre; o chamado e as novidades pedem um artigo
 * específico) sem depender de `ViewChild` entre rotas diferentes.
 */
@Component({
  selector: 'app-support-hub',
  templateUrl: './support-hub.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe, NgClass, A11yModule, RouterLink, MatButtonModule, MatExpansionModule, MatFormFieldModule, MatIconModule,
    MatInputModule, MatProgressSpinnerModule, MatTooltipModule, HelpContentComponent, ButtonComponent,
    SupportEmptyStateComponent, SupportStatusChipComponent,
  ],
})
export class SupportHubComponent implements OnDestroy {
  private readonly ajuda = inject(HelpService);
  private readonly telemetria = inject(HelpTelemetryService);
  private readonly suporte = inject(SupportService);
  private readonly router = inject(Router);
  protected readonly tutorial = inject(TutorialService);
  protected readonly hub = inject(SupportHubService);
  private readonly anunciador = inject(LiveAnnouncer);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  @ViewChild('campoBusca') private readonly campoBusca?: ElementRef<HTMLInputElement>;

  protected readonly rotulosDePermissao = rotulosDePermissao;
  protected readonly carregandoBase = signal(true);
  protected readonly baseIndisponivel = signal(false);
  protected readonly termoBuscado = signal('');
  protected readonly artigosDaRota = signal<HelpArticle[]>([]);
  protected readonly artigosDaBusca = signal<HelpArticle[]>([]);
  protected readonly retornoNegativo = signal<string | null>(null);
  protected readonly motivos: ReadonlyArray<{ valor: HelpFeedbackReason; texto: string }> = [
    { valor: 'informacao_ausente', texto: 'Não encontrei a informação' },
    { valor: 'passos_diferentes', texto: 'Os passos são diferentes da tela' },
    { valor: 'nao_resolveu', texto: 'Não resolveu minha dúvida' },
    { valor: 'outro', texto: 'Outro motivo' },
    { valor: 'erro_na_pagina', texto: 'Encontrei um erro nesta página' },
  ];
  protected readonly erroNaPagina = 'Encontrei um erro nesta página';
  /** Motivo já escolhido ao abrir o "Não" pelo link "Reportar um erro nesta página". */
  protected readonly motivoInicial = signal<HelpFeedbackReason | null>(null);
  /** Relato de erro depois do voto: formulário aberto, envios em curso, falhas e confirmados pela API. */
  protected readonly relatoAberto = signal<string | null>(null);
  protected readonly relatosPendentes = signal<ReadonlySet<string>>(new Set());
  protected readonly relatosComFalha = signal<ReadonlySet<string>>(new Set());
  protected readonly relatosRegistrados = signal<ReadonlySet<string>>(new Set());
  /** Votos com registro confirmado pela API. */
  protected readonly resolvidos = signal<ReadonlySet<string>>(new Set());
  protected readonly votosPendentes = signal<ReadonlySet<string>>(new Set());
  protected readonly votosComFalha = signal<ReadonlySet<string>>(new Set());
  protected readonly statusAtual = signal<SupportStatus | null>(null);
  protected readonly statusIndisponivel = signal(false);

  protected readonly artigosExibidos = computed(() => (
    this.termoBuscado() ? this.artigosDaBusca().slice(0, TAMANHO_RESULTADOS) : this.artigosDaRota().slice(0, TAMANHO_NESTA_TELA)
  ));

  protected readonly linhaDeStatus = computed<LinhaDeStatus | null>(() => {
    const status = this.statusAtual();
    if (!status) {
      return null;
    }
    const incidentePrincipal = ordenarIncidentesPorGravidade(status.active)[0] ?? null;
    return {
      categoria: CATEGORIA_POR_ESTADO[status.state],
      texto: incidentePrincipal ? incidentePrincipal.title : FRASE_POR_ESTADO[status.state],
      comIncidente: incidentePrincipal !== null,
      severidade: incidentePrincipal && !this.tituloRepeteSeveridade(incidentePrincipal) ? incidentePrincipal.severity : null,
    };
  });

  /**
   * Mesma regra de `support-incident-banner`: quando o título já começa com
   * o rótulo da severidade ("Instabilidade na emissão de notas fiscais"),
   * repetir o selo antes do título seria a mesma palavra duas vezes.
   */
  private tituloRepeteSeveridade(incidente: SupportIncident): boolean {
    const rotulo = SELOS['severidade'][incidente.severity]?.rotulo ?? incidente.severity;
    return incidente.title.toLowerCase().startsWith(rotulo.toLowerCase());
  }

  private readonly digitacao$ = new Subject<string>();
  private readonly assinaturas = new Subscription();
  private carregouUmaVez = false;
  private houveInteracaoNestaAbertura = false;
  private artigosLidosNestaAbertura = new Set<string>();
  private termosSemResultadoAvisados = new Set<string>();
  private semResultadoTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    this.assinaturas.add(
      this.digitacao$.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(termo => {
          this.termoBuscado.set(termo);
          if (termo.trim().length === 0) {
            return of<HelpArticle[]>([]);
          }
          return this.ajuda.buscar(termo, this.router.url);
        }),
      ).subscribe(resultados => {
        this.artigosDaBusca.set(resultados);
        this.avaliarSemResultado(this.termoBuscado().trim(), resultados);
      }),
    );

    // Abriu (ou pediu um artigo específico enquanto já estava aberto): carrega a base
    // uma vez, foca a busca e reseta o que é por-abertura (deflexão, leitura, resolvidos).
    effect(() => {
      if (!this.hub.aberto()) {
        return;
      }
      const slug = this.hub.artigoAlvo();
      this.aoAbrir(slug);
    });
  }

  ngOnDestroy(): void {
    this.assinaturas.unsubscribe();
    clearTimeout(this.semResultadoTimer);
  }

  protected fechar(): void {
    this.hub.fechar();
  }

  protected aoDigitar(valor: string): void {
    this.houveInteracaoNestaAbertura = true;
    this.digitacao$.next(valor);
  }

  protected artigoExpandido(artigo: HelpArticle): void {
    this.houveInteracaoNestaAbertura = true;
    if (this.artigosLidosNestaAbertura.has(artigo.slug)) {
      return;
    }
    this.artigosLidosNestaAbertura.add(artigo.slug);
    this.telemetria.registrar({
      surface: 'painel',
      ...(this.ajuda.versaoEditorial ? { editorial_version: this.ajuda.versaoEditorial } : {}),
      query: this.termoBuscado() || null,
      route: this.router.url,
      article_slug: artigo.slug,
      outcome: 'leu_artigo',
    });
  }

  protected marcarResolvido(artigo: HelpArticle): void {
    if (this.votoEncerradoOuEmEnvio(artigo.slug)) return;
    this.retornoNegativo.set(null);
    this.votar(artigo.slug, {
      surface: 'painel',
      ...(this.ajuda.versaoEditorial ? { editorial_version: this.ajuda.versaoEditorial } : {}),
      query: this.termoBuscado() || null,
      route: this.router.url,
      article_slug: artigo.slug,
      shown_slugs: this.artigosExibidos().map(item => item.slug),
      outcome: 'resolveu',
    });
  }

  /** Falha mantém o formulário aberto, com o motivo e o texto digitados, para enviar de novo. */
  protected marcarNaoResolvido(artigo: HelpArticle, motivo: string, texto: string): void {
    if (this.votoEncerradoOuEmEnvio(artigo.slug)) return;
    const reason = this.motivos.find(item => item.valor === motivo)?.valor;
    this.votar(artigo.slug, {
      surface: 'painel',
      ...(this.ajuda.versaoEditorial ? { editorial_version: this.ajuda.versaoEditorial } : {}),
      query: this.termoBuscado() || null,
      route: this.router.url,
      article_slug: artigo.slug,
      shown_slugs: this.artigosExibidos().map(item => item.slug),
      outcome: 'nao_resolveu',
      ...(reason ? { reason } : {}),
      ...(texto.trim() ? { reason_text: texto.trim().slice(0, 500) } : {}),
    }, () => this.retornoNegativo.set(null));
  }

  /**
   * Link "Reportar um erro nesta página", visível antes e depois do voto.
   * Antes do voto, abre o mesmo formulário do "Não" com o motivo escolhido;
   * depois, abre o relato, que não muda o voto já registrado.
   */
  protected reportarErro(artigo: HelpArticle): void {
    if (this.resolvidos().has(artigo.slug)) {
      this.relatosRegistrados.update(atual => semItem(atual, artigo.slug));
      this.relatoAberto.set(artigo.slug);
      this.levarAoFormulario(`relato-${artigo.slug}`, ANUNCIO_DO_RELATO);
      return;
    }
    this.motivoInicial.set('erro_na_pagina');
    this.retornoNegativo.set(artigo.slug);
    this.levarAoFormulario(`motivo-${artigo.slug}`, ANUNCIO_DO_RETORNO);
  }

  protected abrirNegativo(artigo: HelpArticle): void {
    this.motivoInicial.set(null);
    this.retornoNegativo.set(artigo.slug);
    this.levarAoFormulario(`motivo-${artigo.slug}`, ANUNCIO_DO_RETORNO);
  }

  /**
   * O formulário aparece abaixo do botão: o foco vai ao primeiro campo e o
   * leitor de tela ouve, sem interromper, que ele abriu. Depois da renderização
   * (o campo nasce com o sinal), como o foco da busca em `aoAbrir`.
   */
  private levarAoFormulario(idDoCampo: string, anuncio: string): void {
    setTimeout(() => {
      const campo = this.host.nativeElement.ownerDocument.getElementById(idDoCampo);
      if (!campo || !this.host.nativeElement.contains(campo)) {
        return;
      }
      campo.focus();
      void this.anunciador.announce(anuncio, 'polite');
    });
  }

  /** "Relato registrado" só depois da resposta de sucesso; a falha mantém o texto para reenviar. */
  protected enviarRelato(artigo: HelpArticle, texto: string): void {
    const slug = artigo.slug;
    if (this.relatosPendentes().has(slug)) return;
    this.relatosPendentes.update(atual => new Set(atual).add(slug));
    this.relatosComFalha.update(atual => semItem(atual, slug));
    this.assinaturas.add(
      this.telemetria.enviarVoto({
        surface: 'painel',
        ...(this.ajuda.versaoEditorial ? { editorial_version: this.ajuda.versaoEditorial } : {}),
        route: this.router.url,
        article_slug: slug,
        outcome: 'reportou_erro',
        reason: 'erro_na_pagina',
        ...(texto.trim() ? { reason_text: texto.trim().slice(0, 500) } : {}),
      }).subscribe({
        next: () => {
          this.relatosPendentes.update(atual => semItem(atual, slug));
          this.relatosRegistrados.update(atual => new Set(atual).add(slug));
          this.relatoAberto.set(null);
        },
        error: () => {
          this.relatosPendentes.update(atual => semItem(atual, slug));
          this.relatosComFalha.update(atual => new Set(atual).add(slug));
        },
      }),
    );
  }

  private votoEncerradoOuEmEnvio(slug: string): boolean {
    return this.resolvidos().has(slug) || this.votosPendentes().has(slug);
  }

  /** "Obrigado. Retorno registrado." só depois da resposta de sucesso (achado A1 da revisão Astra). */
  private votar(slug: string, payload: HelpInteractionPayload, aoConfirmar?: () => void): void {
    this.votosPendentes.update(atual => new Set(atual).add(slug));
    this.votosComFalha.update(atual => semItem(atual, slug));
    this.assinaturas.add(
      this.telemetria.enviarVoto(payload).subscribe({
        next: () => {
          this.votosPendentes.update(atual => semItem(atual, slug));
          this.resolvidos.update(atual => new Set(atual).add(slug));
          aoConfirmar?.();
        },
        error: () => {
          this.votosPendentes.update(atual => semItem(atual, slug));
          this.votosComFalha.update(atual => new Set(atual).add(slug));
        },
      }),
    );
  }

  /** Link "Abrir chamado sobre esta tela": nunca bloqueia a navegação, só registra quando houve busca ou leitura. */
  protected aoAbrirChamado(): void {
    this.hub.fechar();
    if (!this.houveInteracaoNestaAbertura) {
      return;
    }
    this.telemetria.registrar({
      surface: 'painel',
      ...(this.ajuda.versaoEditorial ? { editorial_version: this.ajuda.versaoEditorial } : {}),
      query: this.termoBuscado() || null,
      route: this.router.url,
      outcome: 'abriu_chamado',
    });
  }

  protected rotaAtual(): string {
    return this.router.url.split('?')[0];
  }

  protected verTour(): void {
    if (!this.tutorial.temTourNaRota()) {
      return;
    }
    this.hub.fechar();
    this.tutorial.iniciarTourDaRota();
  }

  protected recarregarBase(): void {
    this.carregouUmaVez = false;
    this.aoAbrir(this.hub.artigoAlvo());
  }

  /**
   * `slugAlvo` presente (veio de "Artigos relacionados" ou "Ver artigo",
   * fora desta rota): mostra só o artigo pedido, sem misturar com a lista
   * "Nesta tela" — as duas fontes são requisições paralelas e independentes,
   * e deixar as duas escreverem `artigosDaRota` teria corrida (a que
   * responder por último vencia e podia apagar o artigo pedido).
   */
  private aoAbrir(slugAlvo: string | null): void {
    this.houveInteracaoNestaAbertura = false;
    this.artigosLidosNestaAbertura = new Set();
    this.termosSemResultadoAvisados = new Set();
    this.resolvidos.set(new Set());
    this.votosComFalha.set(new Set());
    this.retornoNegativo.set(null);
    this.motivoInicial.set(null);
    this.relatoAberto.set(null);
    this.relatosComFalha.set(new Set());
    this.relatosRegistrados.set(new Set());
    setTimeout(() => this.campoBusca?.nativeElement.focus());

    if (!this.statusAtual() && !this.statusIndisponivel()) {
      this.carregarStatus();
    }

    if (slugAlvo) {
      this.carregarArtigoAlvo(slugAlvo);
    } else {
      this.assinaturas.add(
        this.ajuda.paraRota(this.router.url).subscribe(artigos => this.artigosDaRota.set(artigos)),
      );
    }

    if (this.carregouUmaVez) {
      return;
    }
    this.carregouUmaVez = true;
    this.carregandoBase.set(true);
    this.baseIndisponivel.set(false);

    this.assinaturas.add(
      this.ajuda.baseDisponivel().subscribe(disponivel => {
        this.baseIndisponivel.set(!disponivel);
        this.carregandoBase.set(false);
      }),
    );
  }

  /** O artigo pedido vira a lista inteira de leitura: o primeiro (e único) item abre sozinho. */
  private carregarArtigoAlvo(slug: string): void {
    this.assinaturas.add(
      this.ajuda.obterPorSlug(slug).subscribe(artigo => this.artigosDaRota.set(artigo ? [artigo] : [])),
    );
  }

  private avaliarSemResultado(termo: string, resultados: HelpArticle[]): void {
    clearTimeout(this.semResultadoTimer);
    // Achado da revisão: base fora do ar devolve [] igual a "busca sem
    // resultado de verdade" — sem esta guarda, a queda da base virava
    // "sem_resultado" na métrica de deflexão (D-HD40).
    if (this.baseIndisponivel() || termo.length < CARACTERES_MINIMOS_PARA_SEM_RESULTADO || resultados.length > 0) {
      return;
    }
    this.semResultadoTimer = setTimeout(() => {
      if (this.termosSemResultadoAvisados.has(termo)) {
        return;
      }
      this.termosSemResultadoAvisados.add(termo);
      this.telemetria.registrar({
        surface: 'painel',
        ...(this.ajuda.versaoEditorial ? { editorial_version: this.ajuda.versaoEditorial } : {}),
        query: termo.slice(0, 150),
        route: this.router.url,
        outcome: 'sem_resultado',
      });
    }, ATRASO_SEM_RESULTADO_MS);
  }

  private carregarStatus(): void {
    this.assinaturas.add(
      this.suporte.status().subscribe({
        next: status => {
          this.statusAtual.set(status);
          this.statusIndisponivel.set(false);
        },
        error: () => this.statusIndisponivel.set(true),
      }),
    );
  }
}
